/**
 * Signal extractor — fires fire-and-forget at session Close.
 *
 * Calls the extract-signals Edge Function which:
 *   1. Uses GPT-4o to extract 3–8 pattern signals from session data
 *   2. Generates embeddings via text-embedding-3-small (server-side, key never client-exposed)
 *   3. Inserts into inner_awakening.pattern_signals with embeddings
 *
 * Awakening (premium) tier only. The full pipeline — extract → cluster → surface
 * → inject — is exclusive to Awakening so the value is never diluted into Practice.
 */
import { supabase } from './supabase'
import { functionHeaders } from './functionAuth'

interface ExtractSignalsInput {
  sessionId: string
  storyText: string
  patternReflection: string
  choiceText: string
  bodyLocation: string
  beliefText: string
  emotion: string
  intensity: number
}

/**
 * Extract and embed signals for a completed session.
 * Safe to call fire-and-forget — never throws. Awakening (premium) only.
 */
export async function extractSignals({
  sessionId, storyText, patternReflection, choiceText,
  bodyLocation, beliefText, emotion, intensity,
}: ExtractSignalsInput): Promise<void> {
  if (!supabase) return

  // Gate: paid tiers only (growth + premium)
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  const { data: profile } = await supabase
    .schema('inner_awakening')
    .from('profiles')
    .select('subscription_tier')
    .eq('id', user.id)
    .maybeSingle()

  const tier = (profile as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
  if (tier !== 'premium') return  // Awakening only: full pipeline is the moat

  // Call Edge Function — fully fire-and-forget after await for the fetch itself
  try {
    await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract-signals`,
      {
        method: 'POST',
        // The person's own token: the function works out who is calling from it, so
        // no user id is sent in the body (#72).
        headers: await functionHeaders(),
        body: JSON.stringify({
          sessionId,
          storyText,
          patternReflection,
          choiceText,
          bodyLocation,
          beliefText,
          emotion,
          intensity,
        }),
      }
    )
  } catch {
    // Non-critical — never surface errors from signal extraction
  }
}
